import { v4 as uuidv4 } from 'uuid';
import { CARD_OPERATION_KIND } from '../constants';
import type { CustomerCardState } from '../types/CustomerCardTypes';
import type { CardOperationKind } from '../types/cardOperation.types';
import type { SearchResult } from '../types/search.types';
import {
    START_OPERATION,
    CONTINUE_OPERATION,
    BACK,
    CANCEL_CARD_OPERATION,
    SELECT_SEARCH_RESULT,
    CLEAR_SEARCH_RESULTS
} from './cardOperationActions';
import type { SEARCH_REQUEST, SEARCH_SUCCESS, SEARCH_FAILURE } from './cardOperationActions';

/**
 * Opens an operation on its search step. operationId rides in `meta`, not `payload` — MV's own
 * middleware compares `action.payload === CARD_OPERATION_KIND.ADD_CUSTOMER` directly, so payload
 * has to stay just the kind.
 */
export const startOperation = (kind: CardOperationKind) =>
    ({ type: START_OPERATION, payload: kind, meta: { operationId: uuidv4() } } as const);

/** Sets the buyer on a deal that has none. Dispatched by the host, with no card mounted to call. */
export const addCustomer = () => startOperation(CARD_OPERATION_KIND.ADD_CUSTOMER);

/** Puts a co-buyer on a deal that has none. Dispatched by the host, with no card mounted to call. */
export const addCoBuyer = () => startOperation(CARD_OPERATION_KIND.ADD_CO_BUYER);

/** Advances the running operation to its edit step, seeding the form from `draft`. Keeps the kind. */
export const continueOperation = (draft: CustomerCardState['draft'] = null) =>
    ({ type: CONTINUE_OPERATION, payload: draft } as const);

/** One step back: `edit` returns to `search`; `search` exits the operation. */
export const goBack = () => ({ type: BACK } as const);

/** Ends the operation without saving — from either step. */
export const cancelCardOperation = () => ({ type: CANCEL_CARD_OPERATION } as const);

export const selectSearchResult = (result: SearchResult) => ({ type: SELECT_SEARCH_RESULT, payload: result } as const);

export const clearSearchResults = () => ({ type: CLEAR_SEARCH_RESULTS } as const);

export type CardOperationAction =
    | ReturnType<typeof startOperation>
    | ReturnType<typeof continueOperation>
    | ReturnType<typeof goBack>
    | ReturnType<typeof cancelCardOperation>
    | ReturnType<typeof selectSearchResult>
    | ReturnType<typeof clearSearchResults>
    | { type: typeof SEARCH_REQUEST; payload: string }
    | { type: typeof SEARCH_SUCCESS; payload: SearchResult[] }
    | { type: typeof SEARCH_FAILURE; payload: string };
